/**
 * Server refusals of the pricing routes as sentences a workshop user can act on (#3034).
 *
 * The server's `message` is developer English and never shown. A refusal is read from its stable
 * `code` (`stale_preview`, `duplicate_rule`, ...), from the idempotency plugin's `error` token, or,
 * for a 400, from the field names of its `issues`. Everything else falls through to the shared
 * `describeApiError` status sentences.
 */

import type { TFunction } from 'i18next';
import { describeApiError } from '@/lib/describeApiError';
import { JournalFullError, PricingAccessError } from '@/hooks/usePartPricing';
import { OwnerChangedError } from './pricingSession';

interface ErrorBody {
  code?: unknown;
  error?: unknown;
  issues?: unknown;
}

function bodyOf(error: unknown): ErrorBody {
  const data = (error as { data?: unknown } | null)?.data;
  return typeof data === 'object' && data !== null ? (data as ErrorBody) : {};
}

/** The server's machine code of this refusal, if it sent one. */
export function pricingErrorCode(error: unknown): string | null {
  const body = bodyOf(error);
  if (typeof body.code === 'string' && body.code !== '') return body.code;
  if (typeof body.error === 'string' && body.error.startsWith('IDEMPOTENCY_')) return body.error;
  return null;
}

/** True for the 409 of a recalculation whose preview no longer matches the data. */
export function isStalePreview(error: unknown): boolean {
  return (error as { status?: unknown } | null)?.status === 409 && pricingErrorCode(error) === 'stale_preview';
}

const FIELD_KEYS: Record<string, string> = {
  companyId: 'party',
  personId: 'party',
  partId: 'target',
  categoryId: 'target',
  priceType: 'priceType',
  value: 'value',
  validFrom: 'validFrom',
  validTo: 'validTo',
  startsOn: 'startsOn',
  endsOn: 'endsOn',
  name: 'name',
  note: 'note',
  marginPercent: 'margin',
  marginOverridePercent: 'margin',
  items: 'items',
  partIds: 'parts',
  previewToken: 'preview',
};

function invalidFields(error: unknown): string[] {
  const { issues } = bodyOf(error);
  if (!Array.isArray(issues)) return [];
  const keys = issues
    .map((issue) => String((issue as { path?: unknown } | null)?.path ?? '').split('.')[0] ?? '')
    .map((path) => FIELD_KEYS[path])
    .filter((key): key is string => key !== undefined);
  return [...new Set(keys)];
}

/**
 * @param t  bound to the `partPricing` namespace
 * @param tc bound to `common` (what `describeApiError` expects)
 */
export function describePricingError(error: unknown, t: TFunction, tc: TFunction): string {
  if (error instanceof PricingAccessError) return t('errors.accessDenied');
  if (error instanceof OwnerChangedError) return t('errors.sessionChanged');
  if (error instanceof JournalFullError) return t('errors.journalFull');

  const status = (error as { status?: unknown } | null)?.status;
  if (status === 400 && pricingErrorCode(error) === null) {
    const fields = invalidFields(error).map((key) => t(`fields.${key}`));
    return fields.length > 0 ? t('errors.invalidFields', { fields: fields.join(', ') }) : t('errors.invalidRequest');
  }

  const code = pricingErrorCode(error);
  if (code !== null) {
    const key = `errorCode.${code.toLowerCase()}`;
    const translated = t(key);
    if (translated !== key) return translated;
  }

  return describeApiError(error, tc, { codePrefix: 'partPricing:errorCode', fallback: t('errors.generic') });
}
